import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useBudgetId } from '../../budget/budget-store';
import { useApi } from '../../api/api-context';
import { TransactionStatus } from '../../transaction/api/transaction-types';
export const useReconcileAccountApi = (options) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const budgetId = useBudgetId();
    const [isPending, setIsPending] = useState(false);
    const transactionsKey = ['transactions', budgetId];
    const accountsKey = ['accounts', budgetId];
    // api call
    const reconcileAccountApi = async (data) => {
        await api.post('/account/reconcile', data);
    };
    const mutation = useMutation({
        mutationFn: reconcileAccountApi,
        onMutate: async (request) => {
            setIsPending(true);
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: transactionsKey });
            await queryClient.cancelQueries({ queryKey: accountsKey });
            // Snapshot the previous values
            const previousTransactions = queryClient.getQueryData(transactionsKey);
            const previousAccounts = queryClient.getQueryData(accountsKey);
            // Optimistically update transactions
            queryClient.setQueryData(transactionsKey, (oldTransactions = []) => {
                let updatedTransactions = oldTransactions.map((transaction) => {
                    if (transaction.accountId === request.id && transaction.status === TransactionStatus.CLEARED) {
                        return {
                            ...transaction,
                            status: TransactionStatus.RECONCILED,
                        };
                    }
                    return transaction;
                });
                // Add the adjustment transaction if provided
                if (request.adjustment) {
                    updatedTransactions = [...updatedTransactions, request.adjustment];
                    updatedTransactions.sort((a, b) => b.dateEpoch - a.dateEpoch || b.id.localeCompare(a.id));
                }
                return updatedTransactions;
            });
            // Optimistically update account
            queryClient.setQueryData(accountsKey, (oldAccounts = []) => {
                return oldAccounts.map((account) => {
                    if (account.id === request.id) {
                        return {
                            ...account,
                            reconciledOn: request.reconciledOn,
                        };
                    }
                    return account;
                });
            });
            if (options?.onSuccess) {
                options.onSuccess();
            }
            return { transactions: previousTransactions, accounts: previousAccounts };
        },
        onError: (_error, _variables, context) => {
            // If the mutation fails, revert the optimistic updates
            if (context?.transactions !== undefined) {
                queryClient.setQueryData(transactionsKey, context.transactions);
            }
            if (context?.accounts !== undefined) {
                queryClient.setQueryData(accountsKey, context.accounts);
            }
        },
        onSettled: () => {
            setIsPending(false);
        },
    });
    return {
        mutate: mutation.mutate,
        isPending,
    };
};
